import ArticleLayout from "@/components/feature/ArticleLayout";
import { Link } from "react-router-dom";
import { articles, siteInfo } from "@/mocks/siteData";
import { usePageSEO } from "@/hooks/usePageSEO";

const article = articles[3];

const SITE_URL = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL
  ? import.meta.env.VITE_SITE_URL.replace(/\/+$/, '')
  : 'https://superbuyspreadsheet.bar';

export default function ArticleTopBrands() {
  usePageSEO({
    title: article.title,
    description: article.excerpt,
    keywords: article.keywords,
    canonicalPath: `/article/${article.slug}`,
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "datePublished": article.date,
      "dateModified": "2026-06-21",
      "author": { "@type": "Organization", "name": "Superbuy Spreadsheet Editorial Team" },
      "publisher": { "@type": "Organization", "name": "Superbuy Spreadsheet", "url": SITE_URL },
      "url": `${SITE_URL}/article/${article.slug}`,
    },
  });
  return (
    <ArticleLayout title={article.title} subtitle={article.excerpt} image={article.image} date={article.date} keywords={article.keywords}>
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-12 mb-5">The Brand Landscape on Superbuy Spreadsheet</h2>
      <p className="text-base text-foreground-700 mb-5">One of the biggest draws of the Superbuy Spreadsheet is the incredible range of brands available. From global sportswear giants to cult streetwear labels, the spreadsheet connects you with sellers who specialize in specific brand categories. Understanding which brands are best represented and which sellers excel in each brand category is key to getting the most value from your shopping experience.</p>
      <p className="text-base text-foreground-700 mb-5">The spreadsheet is organized to help you navigate by brand effortlessly. Instead of scrolling through endless mixed listings, you can jump directly to the Nike section, browse Supreme offerings, or explore emerging brands that are gaining traction in the community. This brand-focused navigation is what sets the Superbuy Spreadsheet apart from generic product aggregators.</p>

      <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-10">
        <h3 className="text-lg font-bold text-foreground-950 mb-5">Top 10 Brands on Superbuy Spreadsheet in 2026</h3>
        <div className="space-y-4">
          {[
            { brand: "Nike", category: "Sneakers, Apparel", strength: "Huge selection, consistent quality across sellers" },
            { brand: "Adidas", category: "Sneakers, Tracksuits", strength: "Great Yeezy alternatives and classic silhouettes" },
            { brand: "Jordan", category: "Sneakers", strength: "Rare colorways and limited editions available" },
            { brand: "Supreme", category: "Streetwear, Accessories", strength: "Box logo items and seasonal collections" },
            { brand: "Stussy", category: "Hoodies, Tees", strength: "Excellent basics with accurate branding" },
            { brand: "Bape", category: "Hoodies, Jackets", strength: "Iconic camo patterns and shark hoodies" },
            { brand: "New Balance", category: "Sneakers", strength: "Growing selection of 550s and 990 series" },
            { brand: "Carhartt", category: "Jackets, Pants", strength: "Durable workwear at accessible prices" },
            { brand: "Essentials", category: "Hoodies, Sweats", strength: "Minimal logo pieces, excellent fabric quality" },
            { brand: "Stone Island", category: "Jackets, Knits", strength: "Badge detail accuracy, premium materials" },
          ].map((b, i) => (
            <div key={b.brand} className="flex items-start gap-4 p-3 rounded-xl hover:bg-background-200/50 transition-colors">
              <span className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 text-xs font-bold flex-shrink-0">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-foreground-900">{b.brand}</div>
                <div className="text-xs text-foreground-500 mt-0.5">{b.category}</div>
                <div className="text-xs text-foreground-600 mt-1 italic">{b.strength}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-12 mb-5">How to Find the Best Brand Deals</h2>
      <p className="text-base text-foreground-700 mb-5">Each brand section in the spreadsheet typically includes multiple sellers, giving you the ability to compare quality and pricing. For popular brands like Nike and Adidas, you might find five or more sellers offering the same model, each with different batch qualities and price points. The spreadsheet notes often include batch comparisons, helping you decide whether the premium batch is worth the extra cost over the budget version.</p>
      <p className="text-base text-foreground-700 mb-5">For streetwear brands like Supreme and Bape, accuracy of logos, tags, and materials varies significantly between sellers. The spreadsheet community is meticulous about documenting these differences, saving you from receiving a product with obviously incorrect branding.</p>

      <div className="mt-12 p-6 md:p-8 rounded-2xl bg-accent-100/50 border border-accent-200 text-center">
        <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">Browse All Brands Now</h3>
        <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">Discover the full brand directory with verified links, updated pricing, and seller ratings.</p>
        <a href={siteInfo.mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          Explore Brand Directory <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
        </a>
      </div>

      <div className="mt-10 pt-8 border-t border-background-200">
        <p className="text-xs text-foreground-400 italic">Disclaimer: All brand names and trademarks belong to their respective owners. This guide is for informational purposes only.</p>
        <div className="mt-6">
          <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 font-medium cursor-pointer inline-flex items-center gap-1.5">
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i> Back to Homepage
          </Link>
        </div>
      </div>
    </ArticleLayout>
  );
}